import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Alert, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiCheckCircle, FiCloud, FiAlertTriangle } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getTripsCheckClaimById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicyInfoCard from '../../components/Common/PolicyInfoCard';
import ClientInfoSections from '../../components/Common/ClientInfoSections';
import WeatherTriggerParamsSection from '../../components/Common/WeatherTriggerParamsSection';
import './AdminTripsCheckClaimDetail.css';
import { resolvePrecipTrigger } from '../../utils/precipTrigger';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const TriggerItem = ({ label, children, full = false }) => (
    <div className={`cc-trigger-item${full ? ' cc-trigger-item--full' : ''}`}>
        <div className="cc-trigger-label">{label}</div>
        <div className="cc-trigger-value">{children}</div>
    </div>
);

const AdminTripsCheckClaimDetail = () => {
    const navigate = useNavigate();
    const { checkClaimId } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [checkClaim, setCheckClaim] = useState(location.state?.checkClaim || null);

    useEffect(() => {
        const fetchCheckClaim = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getTripsCheckClaimById(checkClaimId);
                // API returns { check_claim: { ... } }
                setCheckClaim(data?.check_claim || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement du check claim'));
            } finally {
                setLoading(false);
            }
        };
        // Always fetch by id to ensure up-to-date data
        if (checkClaimId) fetchCheckClaim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkClaimId]);

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du check claim</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du check claim...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du check claim</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            <FiAlertTriangle className="me-2" />
                            {error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!checkClaim) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du check claim</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de check claim disponible pour l'identifiant: {checkClaimId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    const formatWeatherParameter = (value) => {
        if (value == null || value === '') return 'N/A';
        if (Array.isArray(value)) return value.filter(Boolean).join(', ') || 'N/A';
        return String(value);
    };

    const mapObservationDataToWeatherSections = (observationRows = []) => {
        const rows = Array.isArray(observationRows) ? observationRows : [];
        const weatherData = [];
        const weatherTemp = [];
        const weatherWind = [];

        rows.forEach((row) => {
            const rawTime = row?.time != null ? String(row.time).trim() : '';
            const match = rawTime.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})/);
            const forecast_year = match ? match[1] : null;
            const forecast_month = match ? match[2] : null;
            const forecast_day = match ? match[3] : null;
            const forecast_hour = match ? `${String(match[4]).padStart(2, '0')}:${match[5]}` : (rawTime || null);

            const base = {
                forecast_day,
                forecast_month,
                forecast_year,
                forecast_hour,
            };

            if (row?.prcp != null && row.prcp !== '') {
                weatherData.push({ ...base, value: row.prcp });
            }
            if (row?.temperature != null && row.temperature !== '') {
                weatherTemp.push({ ...base, value: row.temperature });
            }
            if (row?.wind != null && row.wind !== '') {
                weatherWind.push({ ...base, value: row.wind });
            }
        });

        return { weatherData, weatherTemp, weatherWind };
    };

    const subcoverageTrips = checkClaim?.reference_info?.subcoverage_trips;
    const firstSub = Array.isArray(subcoverageTrips) && subcoverageTrips.length > 0
        ? subcoverageTrips[0]
        : (Array.isArray(checkClaim?.reference_info?.SubCoverage) && checkClaim.reference_info.SubCoverage[0])
            || checkClaim?.reference_info?.SubCoverage
            || {};

    const isCumulatedValues = [
        checkClaim?.prcp_is_cumulated,
        checkClaim?.['prcp_is_cumulated '],
        checkClaim?.reference_info?.prcp_is_cumulated,
    ];
    const precip = resolvePrecipTrigger(isCumulatedValues, [checkClaim, firstSub]);
    const displayOrNa = (value) => (value == null || value === '' ? 'N/A' : value);

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiCheckCircle className="me-2" />
                    Détail du check claim
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            <Section title="Informations principales">
                <WeatherTriggerParamsSection
                    triggerType={
                        firstSub?.trigger_type
                        ?? checkClaim?.trigger_type
                        ?? checkClaim?.reference_info?.trigger_type
                    }
                    weatherParameter={formatWeatherParameter(
                        firstSub?.weather_parameter
                        ?? checkClaim?.reference_info?.weather_parameter
                    )}
                    lastModifiedBy={get(checkClaim, ['check_claim_last_modified_by'], null)}
                    lastModifiedAt={get(checkClaim, ['check_claim_last_modified_at'], null)}
                    precipMode={precip.mode}
                    precipThreshold={precip.lowThreshold}
                    precipUpperThreshold={precip.upperThreshold}
                    precipUnit={precip.unit}
                    precipHours={precip.hours}
                    temperatureThreshold={get(checkClaim, ['temperature_trigger_hour_inf'], get(firstSub, ['temperature_trigger_hour_inf'], null))}
                    temperatureHours={get(checkClaim, ['temperature_trigger_number_hours'], get(firstSub, ['temperature_trigger_number_hours'], null))}
                    windThreshold={get(checkClaim, ['wind_trigger_hour_inf'], get(firstSub, ['wind_trigger_hour_inf'], null))}
                    windHours={get(checkClaim, ['wind_trigger_number_hours'], get(firstSub, ['wind_trigger_number_hours'], null))}
                />

                {(() => {
                    const weatherParams = Array.isArray(firstSub?.weather_parameter)
                        ? firstSub.weather_parameter
                        : (firstSub?.weather_parameter ? [firstSub.weather_parameter] : []);
                    const paramsNorm = weatherParams.map((p) => String(p).toLowerCase());
                    const hasPrecip = paramsNorm.length === 0
                        || paramsNorm.some((p) => p.includes('precip'));
                    const hasTemp = paramsNorm.length === 0
                        || paramsNorm.some((p) => p.includes('temp'));
                    const hasWind = paramsNorm.length === 0
                        || paramsNorm.some((p) => p.includes('wind') || p.includes('vent'));

                    const tempTriggered = checkClaim?.temperature_is_triggered_forecast;
                    const precipTriggered = checkClaim?.precipitation_is_triggered_forecast;
                    const windTriggered = checkClaim?.wind_is_triggered_forecast;
                    const tempHours = checkClaim?.temperature_count_hours_triggered;
                    const precipHours = checkClaim?.precipitation_count_hours_triggered;
                    const windHours = checkClaim?.wind_count_hours_triggered;

                    const tempTriggeredObs = checkClaim?.temperature_is_triggered_observation;
                    const precipTriggeredObs = checkClaim?.precipitation_is_triggered_observation;
                    const windTriggeredObs = checkClaim?.wind_is_triggered_observation;
                    const tempHoursObs = checkClaim?.temperature_count_hours_triggered_observation;
                    const precipHoursObs = checkClaim?.precipitation_count_hours_triggered_observation;
                    const windHoursObs = checkClaim?.wind_count_hours_triggered_observation;
                    const countHoursObs = checkClaim?.count_hours_triggered_observation;

                    const boolBadge = (value) => {
                        if (value === null || value === undefined) {
                            return <Badge bg="warning">En attente de traitement</Badge>;
                        }
                        return (
                            <Badge bg={value === true ? 'success' : 'secondary'}>
                                {value === true ? 'Oui' : 'Non'}
                            </Badge>
                        );
                    };

                    const statusOrNa = (applicable, value) => (
                        !applicable
                            ? <Badge bg="secondary">Non applicable</Badge>
                            : boolBadge(value)
                    );

                    const hoursValue = (applicable, value) => {
                        if (!applicable) return '—';
                        if (value === null || value === undefined) return '—';
                        return value;
                    };

                    let isTriggeredObs = checkClaim?.is_triggered_observation;
                    if (isTriggeredObs === undefined && checkClaim?.CheckClaims) {
                        isTriggeredObs = checkClaim.CheckClaims.is_triggered_observation;
                    }

                    return (
                        <div className="cc-trigger-panels">
                            <section className="cc-trigger-panel" aria-label="Forecast">
                                <h3 className="cc-trigger-panel__title">Forecast</h3>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label={<><FiCheckCircle className="me-1" aria-hidden />Forecast déclenché</>} full>
                                        {boolBadge(checkClaim?.is_triggered_forecast)}
                                    </TriggerItem>
                                </div>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Température déclenchée">
                                        {statusOrNa(hasTemp, tempTriggered)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures température">
                                        {hoursValue(hasTemp, tempHours)}
                                    </TriggerItem>
                                </div>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Précipitation déclenchée">
                                        {statusOrNa(hasPrecip, precipTriggered)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures précipitation">
                                        {hoursValue(hasPrecip, precipHours)}
                                    </TriggerItem>
                                </div>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Vent déclenché">
                                        {statusOrNa(hasWind, windTriggered)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures vent">
                                        {hoursValue(hasWind, windHours)}
                                    </TriggerItem>
                                </div>
                            </section>

                            <section className="cc-trigger-panel" aria-label="Observation">
                                <h3 className="cc-trigger-panel__title">Observation</h3>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Observation déclenchée">
                                        {boolBadge(isTriggeredObs)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures déclenchées">
                                        {countHoursObs === null || countHoursObs === undefined
                                            ? '—'
                                            : countHoursObs}
                                    </TriggerItem>
                                </div>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Température déclenchée">
                                        {statusOrNa(hasTemp, tempTriggeredObs)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures température">
                                        {hoursValue(hasTemp, tempHoursObs)}
                                    </TriggerItem>
                                </div>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Précipitation déclenchée">
                                        {statusOrNa(hasPrecip, precipTriggeredObs)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures précipitation">
                                        {hoursValue(hasPrecip, precipHoursObs)}
                                    </TriggerItem>
                                </div>

                                <div className="cc-trigger-grid">
                                    <TriggerItem label="Vent déclenché">
                                        {statusOrNa(hasWind, windTriggeredObs)}
                                    </TriggerItem>
                                    <TriggerItem label="Nb heures vent">
                                        {hoursValue(hasWind, windHoursObs)}
                                    </TriggerItem>
                                </div>
                            </section>
                        </div>
                    );
                })()}
            </Section>

            <PolicyInfoCard
                companyName={checkClaim?.partner_info?.company_name}
                policyCode={checkClaim?.policy_info?.policy_code}
                reservationNumber={checkClaim?.activity_info?.reservation_number}
                stayStartDate={checkClaim?.activity_info?.activity_start_date}
                stayEndDate={checkClaim?.activity_info?.activity_end_date}
                newStayStartDate={checkClaim?.activity_info?.new_trip_start_date}
                newStayEndDate={checkClaim?.activity_info?.new_trip_end_date}
                effectiveStartDate={checkClaim?.activity_info?.trip_effective_start_date}
                effectiveEndDate={checkClaim?.activity_info?.trip_effective_end_date}
                newEffectiveStartDate={checkClaim?.activity_info?.new_trip_effective_start_date}
                newEffectiveEndDate={checkClaim?.activity_info?.new_trip_effective_end_date}
                referenceName={checkClaim?.reference_info?.reference_name}
                address={
                    checkClaim?.activity_info?.combined_address
                    || [
                        checkClaim?.activity_info?.activity_address,
                        checkClaim?.activity_info?.activity_zip_code,
                        checkClaim?.activity_info?.activity_city,
                        checkClaim?.activity_info?.activity_country,
                    ].filter(Boolean).join(', ')
                    || checkClaim?.forecast_info?.address
                }
                latitude={checkClaim?.activity_info?.latitude ?? checkClaim?.forecast_info?.latitude}
                longitude={checkClaim?.activity_info?.longitude ?? checkClaim?.forecast_info?.longitude}
                dailySumInsuredMaintain={checkClaim?.policy_info?.daily_sum_insured_maintain}
                dailySumInsuredCancellation={checkClaim?.policy_info?.daily_sum_insured_cancellation}
                totalSumInsuredMaintain={checkClaim?.policy_info?.total_sum_insured_maintain}
                totalSumInsuredCancellation={checkClaim?.policy_info?.total_sum_insured_cancellation}
            />

            <ClientInfoSections
                clientInfo={checkClaim?.client_info}
                partnerInfo={checkClaim?.partner_info}
                submissionDateFallback={checkClaim?.policy_info?.date_rib_submission}
            />

            <Section title="Informations de référence">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Nom référence</Label>
                        <Value>{get(checkClaim, ['reference_info', 'reference_name'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Paramètre météo</Label>
                        <Value>{formatWeatherParameter(firstSub?.weather_parameter ?? checkClaim?.reference_info?.weather_parameter)}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Déclenchement cumulé</Label>
                        <Value>
                            <Badge bg={precip.isCumulated ? 'success' : 'secondary'}>
                                {precip.isCumulated ? 'Oui' : 'Non'}
                            </Badge>
                            <span className="ms-2 text-muted small">({precip.modeLabel})</span>
                        </Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    {precip.isCumulated ? (
                        <>
                            <Col md={4}>
                                <Label>Seuil bas cumulé des précipitations (mm)</Label>
                                <Value>{displayOrNa(precip.lowThreshold)}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Seuil haut cumulé des précipitations (mm)</Label>
                                <Value>{displayOrNa(precip.upperThreshold)}</Value>
                            </Col>
                        </>
                    ) : (
                        <>
                            <Col md={4}>
                                <Label>Seuil horaire inférieur des précipitations (mm/h)</Label>
                                <Value>{displayOrNa(precip.lowThreshold)}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Seuil horaire supérieur des précipitations (mm/h)</Label>
                                <Value>{displayOrNa(precip.upperThreshold)}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Nombre d’heures de déclenchement des précipitations</Label>
                                <Value>{displayOrNa(precip.hours)}</Value>
                            </Col>
                        </>
                    )}
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Seuil de déclenchement de la température</Label>
                        <Value>{get(checkClaim, ['temperature_trigger_hour_inf'], get(firstSub, ['temperature_trigger_hour_inf'], 'N/A'))}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Nombre d’heures de déclenchement de la température</Label>
                        <Value>{get(checkClaim, ['temperature_trigger_number_hours'], get(firstSub, ['temperature_trigger_number_hours'], 'N/A'))}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Seuil de déclenchement du vent</Label>
                        <Value>{get(checkClaim, ['wind_trigger_hour_inf'], get(firstSub, ['wind_trigger_hour_inf'], 'N/A'))}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Nombre d’heures de déclenchement du vent</Label>
                        <Value>{get(checkClaim, ['wind_trigger_number_hours'], get(firstSub, ['wind_trigger_number_hours'], 'N/A'))}</Value>
                    </Col>
                </Row>
            </Section>

            {/* Informations prévision */}
            {checkClaim?.forecast_info && checkClaim.forecast_info.forecast_id && (
                <Section title="Informations prévision">
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Source de données</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'data_source'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label><FiCloud className="me-1" />Total mm précipitation</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'total_mm_prc'])}</Value>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Coordonnées</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'latitude'])}, {get(checkClaim, ['forecast_info', 'longitude'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Jour du trip</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'trip_day'], 'N/A')}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Date init</Label>
                            <Value>
                                {get(checkClaim, ['forecast_info', 'meteomatics_init_day'])}/
                                {get(checkClaim, ['forecast_info', 'meteomatics_init_month'])}/
                                {get(checkClaim, ['forecast_info', 'meteomatics_init_year'])}
                            </Value>
                        </Col>
                        <Col md={4}>
                            <Label>Heure exécution (UTC)</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'meteomatics_run_hour_utc'])}</Value>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Adresse</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'address'], 'N/A')}</Value>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Label>Dernière modification</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'forecast_last_modified_at'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Modifié par</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'forecast_last_modified_by'])}</Value>
                        </Col>
                    </Row>
                    <WeatherDataSections
                        weatherData={checkClaim.forecast_info.weather_data}
                        weatherTemp={checkClaim.forecast_info.weather_temp}
                        weatherWind={checkClaim.forecast_info.weather_wind}
                        enableCopy
                        tableSize="sm"
                        bordered
                        className="mt-3"
                        idPrefix="trips-check-claim"
                    />
                </Section>
            )}

            <Section title="Observations">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Dernière observation ID</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['last_observation_info', 'last_observation_id'], 'N/A')}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Source de données</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_data_source'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label><FiCloud className="me-1" />Total mm précipitation</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_total_mm_prc'], 'N/A')}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Station</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_station_name'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>ID Station</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_station_id'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Coordonnées station</Label>
                        <Value>
                            {get(checkClaim, ['last_observation_info', 'last_latitude_station'], 'N/A')}, {get(checkClaim, ['last_observation_info', 'last_longitude_station'], 'N/A')}
                        </Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Date d'initialisation</Label>
                        <Value>
                            {get(checkClaim, ['last_observation_info', 'last_observation_init_day'], '—')}/
                            {get(checkClaim, ['last_observation_info', 'last_observation_init_month'], '—')}/
                            {get(checkClaim, ['last_observation_info', 'last_observation_init_year'], '—')}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Heure d'exécution</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_observation_run_hour'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Dernière modification</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_observation_last_modified_at'], 'N/A')}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Modifié par</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_observation_last_modified_by'], 'N/A')}</Value>
                    </Col>
                </Row>
                {(() => {
                    const observationRows = checkClaim?.last_observation_info?.last_observation_data;
                    if (!Array.isArray(observationRows) || observationRows.length === 0) {
                        return (
                            <p className="text-muted mb-0 mt-2">Aucune donnée d'observation disponible.</p>
                        );
                    }
                    const { weatherData, weatherTemp, weatherWind } = mapObservationDataToWeatherSections(observationRows);
                    return (
                        <WeatherDataSections
                            weatherData={weatherData}
                            weatherTemp={weatherTemp}
                            weatherWind={weatherWind}
                            titlePrefix="Données d'observation"
                            enableCopy
                            tableSize="sm"
                            bordered
                            className="mt-3"
                            idPrefix="trips-check-claim-observation"
                        />
                    );
                })()}
            </Section>
        </div>
    );
};

export default AdminTripsCheckClaimDetail;

